import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return (
      <section className="bg-white min-h-screen flex items-center justify-center">
        <p className="text-xs font-mono uppercase tracking-[.14em] text-[#555555]">Loading…</p>
      </section>
    );
  }

  if (post === null) {
    return (
      <section className="bg-white text-[#121212] py-24 md:py-32" data-testid="post-not-found">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="font-disp text-[clamp(32px,4vw,56px)] leading-[1.05] tracking-tight font-normal text-[#121212] mb-6">
            Entry not found.
          </h1>
          <Link
            to="/blog"
            className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020]"
          >
            Back to the Journal →
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <Seo
        title={`${post.title} — #whatshesaidproject`}
        description={post.excerpt || "Essays and editorial writing from #whatshesaidproject."}
        path={`/blog/${post.slug}`}
      />
      <article className="bg-white text-[#121212] pt-12 pb-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            to="/blog"
            data-testid="post-back-link"
            className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] hover:text-[#e02020] inline-block mb-10"
          >
            ← Journal
          </Link>
          <div className="flex items-center gap-3 text-xs font-mono text-[#6b6b6b] mb-4">
            <span>
              {post.published_at
                ? new Date(post.published_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : ""}
            </span>
            <span>&bull;</span>
            <span className="text-[#e02020] uppercase tracking-[.14em]">{post.category}</span>
          </div>
          <h1
            data-testid="post-title"
            className="font-disp text-[clamp(32px,4.5vw,56px)] leading-[1.05] tracking-tight font-normal text-[#121212] mb-6 text-balance"
          >
            {post.title}
          </h1>
          {post.author && (
            <p className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] mb-10">
              By {post.author}
            </p>
          )}
          {post.featured_image && (
            <figure className="relative aspect-[16/9] bg-[#f4f4f4] border border-[#e5e5e5] overflow-hidden mb-10">
              <img
                src={post.featured_image}
                alt={post.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </figure>
          )}
          <div
            className="space-y-6 text-[17px] leading-[1.75] text-[#333333] [&_h2]:font-disp [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:font-disp [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-3 [&_blockquote]:border-l-2 [&_blockquote]:border-[#e02020] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#555555] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-[#e02020] [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: post.content || "" }}
          />
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-12 pt-8 border-t border-[#e5e5e5]">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] border border-[#e5e5e5] px-3 py-1.5"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </article>
    </>
  );
};

export default BlogPostPage;
